import { z } from "zod";

import {
  activeFormFieldKeys,
  buildFormResponseDataSchema,
  isBusinessSpec,
  type FormVersionSchemaType,
} from "./form-templates.js";

const value = z.union([
  z.number().finite(),
  z.boolean(),
  z.string().max(120),
  z.array(z.string().max(120)).max(100),
]);
export const ScenarioParametersSchema = z
  .record(z.string().regex(/^[a-z][a-z0-9_]{0,62}$/), value)
  .refine((v) => Object.keys(v).length > 0 && Object.keys(v).length <= 100);
export const CreateFormScenarioSchema = z
  .object({
    template_version_id: z.string().uuid(),
    scenario_id: z.string().uuid().optional(),
    name: z.string().trim().min(1).max(120),
    parameters: ScenarioParametersSchema,
  })
  .strict();
export const FormScenarioTransitionSchema = z
  .object({
    expected_revision: z.string().min(1).max(80),
    action: z.enum(["request", "approve", "reject", "withdraw"]),
  })
  .strict();
const keys = z
  .array(z.string().regex(/^[a-z][a-z0-9_]{0,62}$/))
  .max(100)
  .refine((v) => new Set(v).size === v.length);
export const ApplyFormScenarioSchema = z
  .object({
    scenario_revision_id: z.string().uuid(),
    expected_response_revision: z.string().min(1).max(80),
    selected_keys: keys,
    replace_keys: keys,
  })
  .strict()
  .refine((v) => v.replace_keys.every((key) => v.selected_keys.includes(key)));
export type ScenarioParameters = z.infer<typeof ScenarioParametersSchema>;
export const FormScenarioRevisionSchema = z.object({
  id: z.string().uuid(),
  scenario_id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  owner_user_id: z.string().uuid(),
  template_version_id: z.string().uuid(),
  version_number: z.number().int().positive(),
  name: z.string(),
  parameters: ScenarioParametersSchema,
  sharing_status: z.enum(["private", "pending", "approved", "rejected", "withdrawn"]),
  created_at: z.string(),
  updated_at: z.string(),
  revision: z.string(),
});
export type FormScenarioRevision = z.infer<typeof FormScenarioRevisionSchema>;

export function validateScenarioParameters(
  schema: FormVersionSchemaType,
  parameters: unknown,
): ScenarioParameters {
  const parsed = ScenarioParametersSchema.parse(parameters);
  const eligible = new Set(
    schema.sections
      .flatMap((s) => s.fields)
      .filter((f) => f.scenario_parameter && !f.role && !f.prefill_from)
      .map((f) => f.key),
  );
  if (!isBusinessSpec(schema) || Object.keys(parsed).some((key) => !eligible.has(key)))
    throw new z.ZodError([
      {
        code: z.ZodIssueCode.custom,
        path: ["parameters"],
        message: "ineligible scenario parameter",
      },
    ]);
  buildFormResponseDataSchema(schema, { mode: "draft" }).parse(parsed);
  return parsed;
}

const empty = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Single computation for preview/apply. All non-selected answers stay untouched. */
export function previewFormScenario(
  schema: FormVersionSchemaType,
  current: Record<string, unknown>,
  parameters: ScenarioParameters,
  selectedKeys: readonly string[],
  replaceKeys: readonly string[],
) {
  const valid = validateScenarioParameters(schema, parameters);
  if (
    selectedKeys.some((key) => !Object.hasOwn(valid, key)) ||
    replaceKeys.some((key) => !selectedKeys.includes(key))
  )
    throw new z.ZodError([
      {
        code: z.ZodIssueCode.custom,
        path: ["selected_keys"],
        message: "invalid scenario selection",
      },
    ]);
  const selected = new Set(selectedKeys);
  const replacements = new Set(replaceKeys);
  const candidate = { ...current };
  for (const key of selected)
    if (empty(current[key]) || same(current[key], valid[key]) || replacements.has(key))
      candidate[key] = valid[key];

  // Remove inactive changes until stable: removing one may deactivate another.
  // Never delete historical answers; restore their exact original value instead.
  for (let pass = 0; pass <= selected.size; pass++) {
    const active = activeFormFieldKeys(schema.sections, candidate);
    let restored = false;
    for (const key of selected) {
      if (active.has(key) || same(candidate[key], current[key])) continue;
      if (Object.hasOwn(current, key)) candidate[key] = current[key];
      else delete candidate[key];
      restored = true;
    }
    if (!restored) break;
  }
  const active = activeFormFieldKeys(schema.sections, candidate);
  buildFormResponseDataSchema(schema, { mode: "draft" }).parse(candidate);
  return {
    data: candidate,
    changes: Object.entries(valid).map(([key, proposed]) => ({
      key,
      proposed,
      current: current[key] ?? null,
      selected: selected.has(key),
      active: active.has(key),
      conflict: !empty(current[key]) && !same(current[key], proposed),
      applied: selected.has(key) && !same(candidate[key], current[key]),
    })),
  };
}
